/* PasteHint.jsx -- THE ONE "Tip: paste a row copied from Excel" line, with its "i" (Brian 2026-10-26). Every month grid that pastes
 * (ActualsMonthGrid, ManualRenewalsGrid, the billing-terms MonthGrid) renders this; no grid keeps its own copy of the tip.
 * The tooltip says to show the cents in Excel before copying: Excel copies the DISPLAYED text (diffs/dr-paste-excel.txt finding 1).
 *
 * BEHAVIOUR: opens on hover, on keyboard focus and on a click / tap; closes on mouse-out, blur, Escape (anywhere) or a second click.
 * The icon is a focusable span (tabIndex 0), NOT a form control, so a view-only login's disabled fieldset does not disable it and a
 * viewer can read the tip. A mouse press on it does not take focus from a grid cell (no blur, so no commit); the tooltip ignores the
 * pointer and is portalled to the page body at a fixed position, kept inside the window, so a scrolling grid cannot clip it.
 * The icon is drawn with SVG shapes only (no text), so the tip line's text is unchanged. Records: diffs/dr-paste-tooltip.txt.
 * Plain ASCII (CLAUDE.md 12.5 rule 55). */
import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PASTE_HINT, PASTE_HINT_STYLE, PASTE_TOOLTIP } from './pasteRow';

const TIP_W = 300;   // px, at most; narrower on a narrow window
const ICON_STYLE = { display: 'inline-flex', verticalAlign: 'middle', marginLeft: 4, cursor: 'help', borderRadius: '50%', outlineOffset: 1, fontStyle: 'normal' };
const BOX_STYLE = { position: 'fixed', zIndex: 9999, pointerEvents: 'none', background: '#1A1C2E', color: '#FFFFFF', fontSize: 11, fontStyle: 'normal',
  lineHeight: 1.45, padding: '8px 10px', borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,0.18)', whiteSpace: 'normal', textAlign: 'left' };

/* where the box goes: under the icon, kept 8 px inside the window on both sides; above the icon when there is no room below */
export function placeTip(rect, winW, winH, boxH = 110) {
  const width = Math.max(120, Math.min(TIP_W, winW - 16));
  const left = Math.max(8, Math.min(rect.left, winW - width - 8));
  const below = rect.bottom + 6;
  const top = below + boxH > winH - 8 && rect.top - 6 - boxH >= 8 ? rect.top - 6 - boxH : below;
  return { left, top, width };
}

export default function PasteHint({ style }) {
  const [open, setOpen] = useState(false);
  const [byClick, setByClick] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const id = 'paste-tip-' + useId().replace(/[^A-Za-z0-9_-]/g, '');
  const show = () => setOpen(true);
  const hide = () => { setOpen(false); setByClick(false); };
  useEffect(() => {
    if (!open) return undefined;
    const place = () => { if (ref.current) setPos(placeTip(ref.current.getBoundingClientRect(), window.innerWidth, window.innerHeight)); };
    const onKey = e => { if (e.key === 'Escape') hide(); };   // closes it; the key still reaches whatever has focus
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div data-paste-hint="" style={{ ...PASTE_HINT_STYLE, ...(style || {}) }}>
      {PASTE_HINT}
      <span ref={ref} data-paste-info="" tabIndex={0} role="button" aria-label="How to copy from Excel" aria-expanded={open}
        aria-describedby={open ? id : undefined} title="" style={ICON_STYLE}
        onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}
        onMouseDown={e => e.preventDefault()}   // a press keeps the focus where it is (a grid cell is not blurred)
        onClick={() => { if (open && byClick) hide(); else { setOpen(true); setByClick(true); } }}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (open) hide(); else { setOpen(true); setByClick(true); } } }}>
        <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <circle cx="6" cy="6" r="5.5" fill="#8B8FA3" />
          <circle cx="6" cy="3.4" r="0.95" fill="#FFFFFF" />
          <rect x="5.15" y="5" width="1.7" height="4.4" rx="0.6" fill="#FFFFFF" />
        </svg>
      </span>
      {open && typeof document !== 'undefined' && createPortal(
        <div id={id} role="tooltip" data-paste-tooltip="" style={{ ...BOX_STYLE, left: pos ? pos.left : 8, top: pos ? pos.top : 8, width: pos ? pos.width : TIP_W }}>
          {PASTE_TOOLTIP}
        </div>, document.body)}
    </div>
  );
}
